<template>
  <div>
    <v-container>
      <v-row>
        <v-col cols='12' md='4'>
          <wc-data-box
            :title='basics.title'
            :titleIcon='basics.icon'
            :titleIconColor='basics.iconColor'
            showHelp
            :helpText='helpText'
          >
            <v-card-text>
              <v-switch
                inset
                :loading='toggling'
                v-model='project.detail_required'
                @change='toggleReq'
                :label='requiredLabel'
              ></v-switch>
            </v-card-text>
          </wc-data-box>
        </v-col>
        <v-col cols='12' md='4' v-if='project.detail_required'>
          <wc-data-box
            title='Assigned Details'
            :titleIcon='details.icon'
            :titleIconColor='details.iconColor'
            @add='showPicker = true'
            :loading='adjustingDetails'
          >
            <v-simple-table dense>
              <tbody>
                <tr v-if='projectDetails.length === 0'>
                  <td>No details added</td>
                </tr>
                <tr v-for='d in projectDetails' :key='d.id'>
                  <td>{{ d.detail_display }}</td>
                  <td width='80' class='text-right'>
                    <v-btn
                      small
                      icon
                      color='grey lighten-1'
                      @click='removeDetail(d)'
                      :disabled='adjustingDetails'
                    >
                      <v-icon>mdi-chevron-double-right</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </wc-data-box>
        </v-col>
        <v-col cols='12' md='4' v-if='project.detail_required'>
          <wc-data-box
            title='Available Details'
            v-model='search'
            :loading='adjustingDetails'
            showSearch
            showAdd
            @add='add'
          >
            <v-data-table
              dense
              hide-default-header
              :headers='headers'
              :items='availableDetails'
              :search='search'
              :items-per-page='-1'
              hide-default-footer
            >
              <template v-slot:item.options='{ item }'>
                <v-btn
                  small
                  icon
                  color='grey lighten-1'
                  @click='addDetail(item)'
                  :disabled='adjustingDetails'
                >
                  <v-icon>mdi-chevron-double-left</v-icon>
                </v-btn>
                <v-btn
                  small
                  icon
                  class='mr-n3'
                  color='grey lighten-1'
                  @click='openDetailDialog(item)'
                  :disabled='adjustingDetails'
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </wc-data-box>
        </v-col>
      </v-row>
    </v-container>

    <wc-dialog
      title='Detail'
      :icon='details.icon'
      :showDialog='showAddDetail'
      width='300'
      :disableButtons='!isFormValid'
      :loadingButtons='isSaving'
      @cancel='showAddDetail = false'
      @save='saveDetail'
    >
      <v-form v-model='isFormValid' ref='form'>
        <v-text-field
          label='Description'
          v-model='detailCopy.description'
          :rules='[rules.required]'
        ></v-text-field>
      </v-form>
    </wc-dialog>
  </div>
</template>

<script>
import projectsAPI from '@/modules/projects/api/projects'
import timesheetsAPI from '@/modules/timesheets/api/timesheets'
import formRules from '@/mixins/formRules'
import miscService from '@/services/misc'
import _ from 'lodash'

export default {
  name: 'timesheet-details',
  mixins: [formRules],
  data: () => ({
    isLoading: true,
    toggling: false,
    adjustingDetails: false,
    showAddDetail: false,
    isSaving: false,
    isFormValid: false,
    project: {},
    projectDetails: [],
    allDetails: [],
    detailCopy: {},
    search: '',
    headers: [
      { text: 'Detail', value: 'description' },
      { text: 'Add?', value: 'options', align: 'end' }
    ]
  }),
  computed: {
    basics () {
      return this.$store.state.icons.dataBox.basics
    },
    details () {
      return this.$store.state.icons.dataBox.details
    },
    helpText () {
      let ret = 'This area defines whether staff must add details to their timesheets if they indicate they worked on this project.<br><br>'
      ret += 'If they <strong>should</strong> indicate what detail they worked on, ensure the toggle is blue, to the right, '
      ret += 'and it says "Timesheet details required", then add details to this project from the "Available" list.'
      ret += 'The details listed in the "Assigned" box will be presented to staff when completing their timesheets. <br><br>'
      ret += 'If staff <strong>do not</strong> need to provide details of what they did while working on this project, '
      ret += 'ensure the toggle is to the left and it says "Timesheet details NOT Required".'
      return ret
    },
    requiredLabel () {
      let ret = 'Timesheet details '
      ret += this.project.detail_required ? ' required' : ' NOT required'
      return ret
    },
    orderedDetails () {
      return _.orderBy(this.projectDetails, 'detail_display')
    },
    availableDetails () {
      let toPull = []
      this.projectDetails.forEach(d => {
        toPull.push({ id: d.detail })
      })
      let tmp = _.cloneDeep(this.allDetails)
      return _.pullAllBy(tmp, toPull, 'id')
    }
  },
  mounted () {
    this.getData()
  },
  methods: {
    async getData () {
      this.isLoading = true
      this.project = await projectsAPI.getProject(this.$route.params.id)
      this.projectDetails = await projectsAPI.getProjectTimesheetDetails(this.$route.params.id)
      this.allDetails = await timesheetsAPI.getDivisionTimesheetDetails(this.project.division)
      this.isLoading = false
    },
    async toggleReq () {
      this.toggling = true
      this.project = await projectsAPI.updateProject(this.project)
      this.$emit('updateProject', this.project) // emit the update to the parent so the parent doesn't need it's own call to stay in sync
      this.toggling = false
    },
    async removeDetail (item) {
      this.adjustingDetails = true
      this.projectDetails = miscService.removeObject(this.projectDetails, item)
      await projectsAPI.removeProjectTimesheetDetail(item)
      this.adjustingDetails = false
      this.search = ''
    },
    async addDetail (item) {
      this.adjustingDetails = true
      let tmp = {
        detail: item.id,
        project: this.project.id
      }

      // Because the API returns the wrong type or serialization, we
      // are just using the TMP object and adding the new ID to it
      let tmpID = await projectsAPI.addProjectTimesheetDetail(tmp)
      tmp.detail_display = item.description
      tmp.id = tmpID.id
      this.projectDetails.push(tmp)
      this.adjustingDetails = false
      this.search = ''
    },
    add () {
      this.detailCopy.description = ''
      this.openDetailDialog(this.detailCopy)
    },
    openDetailDialog (d) {
      this.detailCopy = _.cloneDeep(d)
      this.showAddDetail = true
      setTimeout(() => {
        this.$refs.form.resetValidation()
      }, 100)
    },
    async saveDetail () {
      this.isSaving = true
      if (this.detailCopy.id) {
        this.allDetails = miscService.replaceObject(this.allDetails, await timesheetsAPI.updateTimesheetDetail(this.detailCopy))
      } else {
        this.detailCopy.division = this.project.division
        await timesheetsAPI.createTimesheetDetail(this.detailCopy)
        this.allDetails = await timesheetsAPI.getDivisionTimesheetDetails(this.project.division)
      }
      this.isSaving = false
      this.showAddDetail = false
    }
  }
}
</script>
